/* SpawnAnything — mode "Verteidigung" in the sim: wave state machine, gold, buying and upgrading buildings.
   Pure functions on a plain state object; the worker calls them and hands in `S`, its narrow view of the sim
   (see the feature:waves block in sim.worker.js). Everything here is part of the deterministic state: gold, timers,
   spawn positions (sim RNG) and the commands below. Waves, prices and building data all come from `setup`
   (src/features/wavesSetup.js builds it), nothing from the network.

   Commands (feature commands via featCmd / 'upg' in sim.worker.js, validated here so every client drops the same ones):
     { t: 'waves_build', k: key, x, z, r }   buy building `k` at (x / 2, z / 2) metres, rotated r * 90°; build phase only
     { t: 'waves_go' }                       start the next wave now, bonus for every second left on the countdown
     { t: 'upg', id }                        upgrade building `id` (own, standing) for gold; build phase only
   Phases: 0 = build (countdown), 1 = fight, 2 = won (all waves cleared), 3 = lost (core destroyed). */
import { datan2 } from '../dmath.js';

export const WAVES_BUILD = 0, WAVES_FIGHT = 1, WAVES_WON = 2, WAVES_LOST = 3;
const r4 = v => Math.round(v * 1e4) / 1e4;
const own = (o, k) => typeof k === 'string' && Object.prototype.hasOwnProperty.call(o, k);

/** state for one game from the setup message's `waves` block */
export function createWaves(cfg) {
  return {
    cfg, phase: WAVES_BUILD, wave: 0, gold: cfg.startGold | 0, timer: +cfg.first || 30,
    toSpawn: 0, dealt: 0, acc: 0, inc: 0, deadSeen: 0, kills: 0, enemy: 0, es: -1, built: [], // es: enemy army set (wave index, -1 = setup's)
  };
}

/**
 * World-space sim data of a catalog building placed at (x, z) on ground height y (level 1, team 0).
 * Shared by the sim (worker) and the renderer (main) so both use bit-identical data.
 * @param {{ yaw: number, hw: number, hd: number, lv: object[] }} e  catalog entry for one rotation (built at the origin)
 */
export function wavesPlaced(e, key, rot, x, z, y, id) {
  const lv = e.lv.map(L => L && {
    ...L,
    spawn: [r4(L.spawn[0] + x), r4(L.spawn[1] + z)],
    rects: L.rects.map(r => [r4(r[0] + x), r4(r[1] + z), r4(r[2] + x), r4(r[3] + z), r[4]]),
    guns: L.guns.map(g => ({ ...g, x: r4(g.x + x), z: r4(g.z + z), bx: r4(g.bx + x), bz: r4(g.bz + z), y: g.y + y, by: g.by + y })),
  });
  return { id, key, team: 0, level: 1, x, z, rot, yaw: e.yaw, hw: e.hw, hd: e.hd, y, lv };
}

/** may a plot (centre x/z, half extents hw/hd) be built? 0 = yes, 1 = outside the build zone, 2 = overlaps a building */
export function wavesPlotCheck(area, x, z, hw, hd, others) {
  if (x - hw < area[0] || x + hw > area[1] || z - hd < area[2] || z + hd > area[3]) return 1;
  for (const b of others) if (Math.abs(x - b.x) < hw + b.hw - 0.01 && Math.abs(z - b.z) < hd + b.hd - 0.01) return 2;
  return 0;
}

function standing(S) {
  const out = [];
  for (let b = 0; b < S.nB(); b++) if (S.bAlive(b)) out.push(S.bld(b));
  return out;
}

/** handles the wave mode's commands; returns true when `c` was applied (false: rejected or not a wave command) */
export function wavesCmd(W, c, S) {
  if (!c) return false;
  const cfg = W.cfg;
  if (c.t === 'waves_go') {
    if (W.phase !== WAVES_BUILD) return false;
    W.gold += Math.floor(Math.max(0, W.timer)) * (cfg.early | 0);
    startWave(W, S);
    return true;
  }
  if (c.t === 'waves_build') {
    if (W.phase !== WAVES_BUILD || W.built.length >= cfg.maxBuilt || !own(cfg.catalog, c.k)) return false;
    const C = cfg.catalog[c.k], r = c.r;
    if (!Number.isInteger(r) || r < 0 || r > 3 || !Number.isInteger(c.x) || !Number.isInteger(c.z) || Math.abs(c.x) > 4000 || Math.abs(c.z) > 4000) return false;
    if (W.gold < C.cost) return false;
    const x = c.x / 2, z = c.z / 2, e = C.rots[r];
    if (wavesPlotCheck(cfg.area, x, z, e.hw, e.hd, standing(S))) return false;
    const y = S.gAt(x, z);
    W.gold -= C.cost;
    S.addBuilding(wavesPlaced(e, c.k, r, x, z, y, S.nB()));
    W.built.push([c.k, x, z, r, y]);
    return true;
  }
  if (c.t === 'upg') {
    const b = c.id;
    if (W.phase !== WAVES_BUILD || !Number.isInteger(b) || b < 0 || b >= S.nB() || !S.bAlive(b)) return false;
    const B = S.bld(b), L = S.bLevel(b);
    if (B.team !== 0 || L >= 3 || !B.lv[L] || !own(cfg.catalog, B.key)) return false;
    const cost = cfg.catalog[B.key].up[L - 1];
    if (!(cost >= 0) || W.gold < cost) return false;
    W.gold -= cost;
    S.upgrade(b);
    return true;
  }
  return false;
}

function startWave(W, S) {
  const w = W.cfg.waves[W.wave];
  W.phase = WAVES_FIGHT; W.timer = 0; W.toSpawn = w.count; W.dealt = 0; W.acc = 0; W.inc = 0;
  W.enemy = W.wave; S.setEnemy(W.cfg.armies[W.wave]);
  W.deadSeen = S.dead[1];
}

// one unit of the running wave at its edge (round-robin over the wave's edges), facing the core; false = no free slot
function spawnOne(W, S, w) {
  const s = w.from[W.dealt % w.from.length], off = (S.R() - 0.5) * 2 * s[3];
  const x = s[2] ? s[0] + off : s[0], z = s[2] ? s[1] : s[1] + off;
  const t = W.cfg.target;
  if (!S.spawn(x, z, datan2(t[0] - x, t[1] - z))) return false;
  W.dealt++; W.toSpawn--;
  return true;
}

/** once per tick, at the end of step(): gold, spawning, phase changes, win / loss (overrides the default rule) */
export function wavesStep(W, dt, S) {
  const cfg = W.cfg;
  if (W.phase === WAVES_FIGHT) {
    const w = cfg.waves[W.wave];
    const d = S.dead[1];
    if (d > W.deadSeen) { const n = d - W.deadSeen; W.deadSeen = d; W.kills += n; W.gold += n * w.bounty; }
    if (W.toSpawn > 0) {
      W.acc += dt * w.count / w.spawn;
      let n = 0;
      while (W.acc >= 1 && W.toSpawn > 0 && n < 64) { if (!spawnOne(W, S, w)) break; W.acc -= 1; n++; }
    }
    // income buildings (farms, workshops, reactors) pay every second while a wave runs
    W.inc += dt;
    while (W.inc >= 1) {
      W.inc -= 1;
      for (let b = 0; b < S.nB(); b++) {
        if (!S.bAlive(b)) continue;
        const B = S.bld(b);
        if (B.team === 0 && own(cfg.catalog, B.key)) W.gold += cfg.catalog[B.key].income[S.bLevel(b) - 1] | 0;
      }
    }
    if (W.toSpawn <= 0 && S.aliveOf(1) <= 0) {
      W.gold += w.bonus; W.wave++;
      if (W.wave >= cfg.waves.length) { W.wave = cfg.waves.length - 1; W.phase = WAVES_WON; }
      else { W.phase = WAVES_BUILD; W.timer = +cfg.build || 20; }
    }
  } else if (W.phase === WAVES_BUILD) {
    W.timer -= dt;
    if (W.timer <= 0) startWave(W, S);
  }
  if (W.phase < WAVES_WON && !S.bAlive(cfg.core)) W.phase = WAVES_LOST;
  S.end(W.phase >= WAVES_WON, W.phase === WAVES_WON ? 0 : W.phase === WAVES_LOST ? 1 : -1);
}

/** HUD data for the main thread (plain numbers + the list of bought buildings; reading it changes nothing) */
export function wavesInfo(W, S) {
  const core = W.cfg.core;
  return {
    phase: W.phase, wave: W.wave, total: W.cfg.waves.length, gold: W.gold, timer: Math.max(0, W.timer),
    bonus: W.phase === WAVES_BUILD ? Math.floor(Math.max(0, W.timer)) * (W.cfg.early | 0) : 0,
    left: W.phase === WAVES_FIGHT ? W.toSpawn + Math.max(0, S.aliveOf(1)) : 0, enemy: W.enemy, kills: W.kills,
    core: S.bAlive(core) ? S.bHp(core) : 0, built: W.built,
  };
}
